import { useState, type MouseEvent } from "react";
import type { IconName } from "../../icons";
import AlertDialog from "./AlertDialog";

type ConfirmActionOptions = {
  onConfirm: () => Promise<unknown> | undefined;
  title: string;
  description: string;
  /** Replaces `description` if `onConfirm` rejects. */
  errorDescription: string;
  variant: "default" | "destructive";
  actionLabel: string;
  actionIcon?: IconName;
  cancelLabel?: string;
};

/**
 * Owns an AlertDialog's open, pending and error state. Call `open` from any
 * trigger and render `dialogElement` anywhere in the tree (it portals).
 * Confirming keeps the dialog open until `onConfirm` settles — success
 * closes it, failure swaps in `errorDescription` instead of closing as if it
 * had worked.
 */
export const useConfirmAction = ({
  onConfirm,
  description,
  errorDescription,
  cancelLabel = "Abbrechen",
  ...dialogProps
}: ConfirmActionOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const open = () => {
    setFailed(false);
    setIsOpen(true);
  };

  // preventDefault stops Radix's auto-close; closing waits for onConfirm.
  const handleAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsPending(true);
    Promise.resolve(onConfirm())
      .then(
        () => setIsOpen(false),
        () => setFailed(true),
      )
      .finally(() => setIsPending(false));
  };

  const dialogElement = (
    <AlertDialog
      {...dialogProps}
      open={isOpen}
      onOpenChange={setIsOpen}
      cancelLabel={cancelLabel}
      description={failed ? errorDescription : description}
      isPending={isPending}
      onAction={handleAction}
    />
  );

  return { open, dialogElement };
};
